import { formatCourtTagLabel } from "@bw/shared/utils";
import type {
  FacilityResponse,
  PricingRuleResponse,
} from "@bw/shared/types/generated";

export type FacilityReadState = "loading" | "error" | "ready";

export type FacilityRead<T> = Readonly<{
  data: T | undefined;
  state: FacilityReadState;
  onRetry: () => void;
}>;

export function facilityReadState(query: {
  data: unknown;
  isError: boolean;
  isPending: boolean;
  isSuccess: boolean;
}): FacilityReadState {
  if (query.isError || (query.isSuccess && query.data === undefined)) {
    return "error";
  }
  return query.isPending ? "loading" : "ready";
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function formatFacilityAddress(facility: FacilityResponse): string {
  const address = facility.address;
  if (!address?.line1 || !address.city || !address.postal_code) {
    return facility.location;
  }

  return [
    address.line1,
    address.line2?.trim(),
    [address.city, address.state, address.postal_code]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");
}

export function formatFacilityService(service: string): string {
  if (service === "open_play") return "Drop-in";
  return service
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function formatStaffCheckIn(
  available: boolean | null | undefined,
): string {
  if (available === true) return "Available";
  if (available === false) return "Not available";
  return "Unconfirmed";
}

export function formatQueueMode(mode: string): string {
  return (
    {
      auto: "Auto assignment",
      manual: "Manual assignment",
      self_serve: "Self-serve",
    }[mode] ?? "Unavailable"
  );
}

export function formatQueueStrategy(strategy: string): string {
  return (
    {
      fill_existing: "Fill active courts first",
      prefer_fresh: "Prefer fresh courts",
      fresh_only: "Fresh courts only",
    }[strategy] ?? "Unavailable"
  );
}

export function formatLateJoinTiming(policy: string): string {
  return (
    {
      fresh_block: "Start a fresh block",
      extend_existing: "Extend active block",
      inherit_remaining: "Inherit active expiry",
    }[policy] ?? "Unavailable"
  );
}

export function formatRuleRepeat(rule: PricingRuleResponse): string {
  if (rule.repeat_type === "weekly" && rule.days_of_week?.length) {
    const names = rule.days_of_week.map((day) => DAY_NAMES[day]);
    return names.every(Boolean) ? names.join(", ") : "Unavailable";
  }
  if (rule.repeat_type === "never") {
    if (rule.start_date && rule.end_date && rule.start_date !== rule.end_date) {
      return `${rule.start_date} to ${rule.end_date}`;
    }
    return rule.start_date ?? "One time";
  }
  return formatFacilityService(rule.repeat_type);
}

export function formatRuleTime(rule: PricingRuleResponse): string {
  return rule.all_day ? "All day" : `${rule.start_time} – ${rule.end_time}`;
}

export function formatCourtTags(tags: string[]): string {
  return tags.length > 0 ? tags.map(formatCourtTagLabel).join(", ") : "No tags";
}

export function safeActorLabel(
  displayName: string | null | undefined,
  actorId: string | null | undefined,
): string {
  const label = displayName?.trim();
  return !label || label === actorId ? "Actor unavailable" : label;
}
